import { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import Modal from "../../components/Modal";
import { useToast } from "../../lib/toast";

interface Contact { id: string; name: string; role: string | null; phone: string | null; email: string | null; is_primary: number; }
interface Detail {
  user: { id: string; email: string; company_name: string; business_no: string; representative: string; phone: string; address: string | null; status: string; tier: string; created_at: number };
  contacts: Contact[];
  credit: { limit_amount: number; balance: number; updated_at?: number };
  orders_summary: { count: number; total: number };
}
const fmt = (n: number) => (n ?? 0).toLocaleString("ko-KR");

export default function CustomerDetail({ userId, onClose }: { userId: string; onClose: () => void }) {
  const [d, setD] = useState<Detail | null>(null);
  const [loading, setLoading] = useState(true);
  const [addingContact, setAddingContact] = useState(false);
  const [editingCredit, setEditingCredit] = useState(false);
  const { push } = useToast();

  const load = useCallback(() => {
    setLoading(true);
    api.get<Detail>(`/admin/customers/${userId}`).then(setD).finally(() => setLoading(false));
  }, [userId]);
  useEffect(() => { load(); }, [load]);

  const saveContact = async (c: { name: string; role?: string; phone?: string; email?: string; is_primary: boolean }) => {
    try {
      await api.post(`/admin/customers/${userId}/contacts`, c);
      push("담당자 추가됨", "success");
      setAddingContact(false);
      load();
    } catch (e: any) { push(e.message, "error"); }
  };

  const removeContact = async (c: Contact) => {
    if (!window.confirm(`${c.name} 담당자를 삭제할까요?`)) return;
    try {
      await api.post(`/admin/customers/contacts/${c.id}/delete`);
      push("삭제됨", "info");
      load();
    } catch (e: any) { push(e.message, "error"); }
  };

  const saveCredit = async (limit_amount: number) => {
    try {
      await api.post(`/admin/customers/${userId}/credit`, { limit_amount });
      push(`여신 한도가 ${limit_amount.toLocaleString("ko-KR")}원으로 설정됨`, "success");
      setEditingCredit(false);
      load();
    } catch (e: any) { push(e.message, "error"); }
  };

  return (
    <Modal
      title={d?.user.company_name ?? "고객 상세"}
      subtitle={d ? `${d.user.business_no} · ${d.user.representative} · 등급 ${d.user.tier}` : undefined}
      onClose={onClose}
      width={720}
    >
      {loading || !d ? (
        <div className="muted">로딩 중...</div>
      ) : (
        <>
          <div style={{ marginBottom: 16 }}>
            <span className={`badge badge-${d.user.status === "approved" ? "success" : "warning"}`}>{d.user.status}</span>
          </div>

            {/* Stats */}
            <div className="row" style={{ gap: 12, marginBottom: 16 }}>
              <Stat label="누적 주문" value={`${fmt(d.orders_summary.count)}건`} />
              <Stat label="누적 거래액" value={`${fmt(d.orders_summary.total)}원`} />
              <Stat label="여신 한도" value={`${fmt(d.credit.limit_amount)}원`} sub={`미수 ${fmt(d.credit.balance)}원`} />
            </div>

            {/* Credit limit edit */}
            <div className="card" style={{ padding: 16, marginBottom: 16, background: "var(--bg-subtle)" }}>
              <div className="row" style={{ justifyContent: "space-between" }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 15 }}>여신 / 미수금 관리</div>
                  <div className="muted" style={{ fontSize: 15 }}>세금계산서 후불 주문 시 자동 가산, 입금 확인 시 자동 차감</div>
                </div>
                <button className="btn btn-outline btn-sm" onClick={() => setEditingCredit(true)}>한도 변경</button>
              </div>
            </div>

            {/* Contacts */}
            <div className="row" style={{ justifyContent: "space-between", marginBottom: 8 }}>
              <div style={{ fontWeight: 600, fontSize: 15 }}>담당자 {d.contacts.length}명</div>
              <button className="btn btn-primary btn-sm" onClick={() => setAddingContact(true)}>+ 추가</button>
            </div>
            <div className="card">
              <table>
                <thead><tr><th>이름</th><th>역할</th><th>연락처</th><th>이메일</th><th></th></tr></thead>
                <tbody>
                  {d.contacts.length === 0 ? (
                    <tr><td colSpan={5} className="muted" style={{ padding: 24, textAlign: "center" }}>담당자 없음</td></tr>
                  ) : d.contacts.map((c) => (
                    <tr key={c.id}>
                      <td>{c.name} {c.is_primary ? <span className="badge badge-success" style={{ marginLeft: 4 }}>주</span> : null}</td>
                      <td style={{ fontSize: 15 }}>{c.role ?? "—"}</td>
                      <td style={{ fontSize: 15 }}>{c.phone ?? "—"}</td>
                      <td style={{ fontSize: 15 }}>{c.email ?? "—"}</td>
                      <td style={{ textAlign: "right" }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => removeContact(c)} style={{ color: "var(--danger)" }}>삭제</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
      )}

      {addingContact && (
        <ContactForm onClose={() => setAddingContact(false)} onSave={saveContact} />
      )}
      {editingCredit && d && (
        <CreditForm current={d.credit.limit_amount} onClose={() => setEditingCredit(false)} onSave={saveCredit} />
      )}
    </Modal>
  );
}

function ContactForm({ onClose, onSave }: { onClose: () => void; onSave: (c: { name: string; role?: string; phone?: string; email?: string; is_primary: boolean }) => void }) {
  const [f, setF] = useState({ name: "", role: "", phone: "", email: "", is_primary: false });
  return (
    <Modal title="담당자 추가" onClose={onClose} width={440}>
      <form onSubmit={(e) => { e.preventDefault(); onSave(f); }} className="stack">
        <label><div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>이름 *</div>
          <input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} autoFocus /></label>
        <label><div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>역할</div>
          <input value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} placeholder="예: 구매담당, 회계" /></label>
        <label><div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>연락처</div>
          <input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="010-0000-0000" /></label>
        <label><div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>이메일</div>
          <input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></label>
        <label className="row"><input type="checkbox" checked={f.is_primary} onChange={(e) => setF({ ...f, is_primary: e.target.checked })} style={{ width: "auto" }} /> <span>주 담당자로 설정</span></label>
        <div className="row">
          <button type="button" className="btn btn-outline" onClick={onClose} style={{ flex: 1 }}>취소</button>
          <button className="btn btn-primary" style={{ flex: 1 }}>추가</button>
        </div>
      </form>
    </Modal>
  );
}

function CreditForm({ current, onClose, onSave }: { current: number; onClose: () => void; onSave: (limit: number) => void }) {
  const [val, setVal] = useState(current);
  return (
    <Modal title="여신 한도 변경" subtitle="세금계산서 후불 거래 시 적용" onClose={onClose} width={400}>
      <form onSubmit={(e) => { e.preventDefault(); if (val >= 0) onSave(val); }} className="stack">
        <label>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>한도 (원)</div>
          <input type="number" min={0} step={100_000} value={val} onChange={(e) => setVal(Number(e.target.value))} autoFocus />
          <div className="muted" style={{ fontSize: 15, marginTop: 4 }}>
            {val.toLocaleString("ko-KR")}원 · 10만원 단위 권장
          </div>
        </label>
        <div className="row">
          <button type="button" className="btn btn-outline" onClick={onClose} style={{ flex: 1 }}>취소</button>
          <button className="btn btn-primary" style={{ flex: 1 }}>저장</button>
        </div>
      </form>
    </Modal>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="card" style={{ padding: 12, flex: 1, background: "var(--surface)" }}>
      <div className="muted" style={{ fontSize: 15, textTransform: "uppercase", letterSpacing: "0.08em" }}>{label}</div>
      <div style={{ fontWeight: 700, fontFamily: "var(--font-display)", fontSize: 18, marginTop: 2 }}>{value}</div>
      {sub && <div className="muted" style={{ fontSize: 15, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}
